// Consentimiento informado para el tratamiento de datos de salud (LOPDP).
// - Consentimientos: administra las PLANTILLAS versionadas (Centro Medico → Catálogos).
// - ConsentimientosPaciente: panel reutilizable (ficha del paciente y portal) para
//   registrar, listar y REVOCAR el consentimiento de un paciente.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, ShieldCheck, ShieldOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  AutoTextarea,
  BASE_LEGAL, Campo, ConsentItem, ConsentModelo, ConsentRegistro, Modal, Switch,
  TIPO_CONSENTIMIENTO, fieldCls, medRpc,
} from './shared';
import { FirmaCanvas } from './FirmaCanvas';
import { LOCALE } from '@/lib/utils/regional';

const fmt = (s: string | null) => (s ? new Date(s).toLocaleString(LOCALE, { dateStyle: 'medium', timeStyle: 'short' }) : '—');

// ============================================================================
// Pantalla: gestión de plantillas de consentimiento
// ============================================================================
const modeloVacio = { id: '', tipo: 'datos_salud', version: 'v1.0', titulo: '', texto: '', vigente: true, items: [] as ConsentItem[] };
type ModeloForm = typeof modeloVacio;

export function Consentimientos() {
  const qc = useQueryClient();
  const { data: rows = [], isFetching } = useQuery<ConsentModelo[]>({
    queryKey: ['med-consent-modelos'], queryFn: () => medRpc<ConsentModelo[]>('med_consentimiento_modelos', {}),
  });
  const [form, setForm] = useState<ModeloForm | null>(null);

  const guardar = useMutation({
    mutationFn: (f: ModeloForm) => medRpc('med_consentimiento_modelo_guardar', {
      _data: { id: f.id || undefined, tipo: f.tipo, version: f.version, titulo: f.titulo, texto: f.texto, vigente: f.vigente, items: f.items },
    }),
    onSuccess: () => { toast.success('Plantilla guardada'); setForm(null); void qc.invalidateQueries({ queryKey: ['med-consent-modelos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> Consentimientos informados</h1>
          <p className="text-sm text-muted-foreground">Plantillas del texto que firma el paciente (Ley Orgánica de Protección de Datos Personales).</p>
        </div>
        <Button onClick={() => setForm({ ...modeloVacio })}><Plus className="w-4 h-4 mr-1" /> Nueva plantilla</Button>
      </div>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Título</th><th className="py-2 pr-3">Versión</th>
              <th className="py-2 pr-3">Ítems</th><th className="py-2 pr-3">Vigente</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin plantillas</td></tr>}
              {rows.map((m) => (
                <tr key={m.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{TIPO_CONSENTIMIENTO[m.tipo] ?? m.tipo}</td>
                  <td className="py-2 pr-3 font-medium">{m.titulo}</td>
                  <td className="py-2 pr-3 font-mono text-muted-foreground">{m.version}</td>
                  <td className="py-2 pr-3">{m.items?.length ?? 0}</td>
                  <td className="py-2 pr-3">{m.vigente ? <span className="rounded bg-estable-soft px-2 py-0.5 text-xs text-estable">vigente</span> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="py-2 text-right">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" aria-label="Editar"
                      onClick={() => setForm({ id: m.id, tipo: m.tipo, version: m.version, titulo: m.titulo, texto: m.texto, vigente: m.vigente, items: m.items ?? [] })}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {form && <ModeloModal form={form} setForm={setForm} onClose={() => setForm(null)} onSave={() => guardar.mutate(form)} saving={guardar.isPending} />}
    </div>
  );
}

function ModeloModal({ form, setForm, onClose, onSave, saving }: { form: ModeloForm; setForm: (f: ModeloForm) => void; onClose: () => void; onSave: () => void; saving: boolean }) {
  const setItem = (i: number, patch: Partial<ConsentItem>) => setForm({ ...form, items: form.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  const addItem = () => setForm({ ...form, items: [...form.items, { clave: `item${form.items.length + 1}`, etiqueta: '', requerido: false }] });
  const delItem = (i: number) => setForm({ ...form, items: form.items.filter((_, j) => j !== i) });

  return (
    <Modal title={form.id ? 'Editar plantilla' : 'Nueva plantilla'} onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <Campo label="Tipo">
            <select className={fieldCls} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
              {Object.entries(TIPO_CONSENTIMIENTO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Campo>
          <Campo label="Versión"><input className={fieldCls} value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} /></Campo>
          <div className="flex items-end pb-2"><Switch checked={form.vigente} onChange={(v) => setForm({ ...form, vigente: v })} label="Vigente" /></div>
        </div>
        <Campo label="Título *"><input className={fieldCls} value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} /></Campo>
        <Campo label="Texto informado *"><AutoTextarea value={form.texto} onChange={(e) => setForm({ ...form, texto: e.target.value })} placeholder="Fines del tratamiento, base legal, tiempo de conservación, derechos del titular y forma de revocarlo…" /></Campo>

        <div className="space-y-2">
          <div className="flex items-center justify-between"><span className="text-sm font-semibold text-muted-foreground">Cláusulas (checklist)</span><Button size="sm" variant="outline" onClick={addItem}><Plus className="w-4 h-4 mr-1" /> Cláusula</Button></div>
          {form.items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <input className={`${fieldCls} col-span-9`} value={it.etiqueta} onChange={(e) => setItem(i, { etiqueta: e.target.value })} placeholder="Texto de la cláusula" />
              <label className="col-span-2 flex items-center gap-1 text-xs"><input type="checkbox" checked={!!it.requerido} onChange={(e) => setItem(i, { requerido: e.target.checked })} /> obligatoria</label>
              <button className="col-span-1 text-red-500" title="Quitar" aria-label="Quitar" onClick={() => delItem(i)}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={saving} disabled={!form.titulo.trim() || !form.texto.trim()} onClick={onSave}>Guardar</Button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================================
// Panel reutilizable: consentimientos de un paciente (ficha staff y portal)
// ============================================================================
export function ConsentimientosPaciente({ pacienteId, portal = false }: { pacienteId?: string; portal?: boolean }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const key = ['med-consentimientos', pacienteId ?? 'self'];
  const { data: rows = [] } = useQuery<ConsentRegistro[]>({
    queryKey: key, queryFn: () => medRpc<ConsentRegistro[]>('med_consentimientos', pacienteId ? { _paciente: pacienteId } : {}),
  });
  const [abrir, setAbrir] = useState(false);

  const revocar = useMutation({
    mutationFn: (id: string) => medRpc('med_revocar_consentimiento', { _id: id, _motivo: 'Revocado por el titular' }),
    onSuccess: () => { toast.success('Consentimiento revocado'); void qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const activos = rows.filter((r) => r.aceptado && !r.revocado);

  return (
    <section className="space-y-2">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Consentimiento de datos (LOPDP)</h4>
        <Button size="sm" variant="outline" onClick={() => setAbrir(true)}><Plus className="w-4 h-4 mr-1" /> Registrar consentimiento</Button>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-pendiente">Este paciente aún no tiene un consentimiento registrado para el tratamiento de sus datos de salud.</p>
      ) : (
        <ul className="text-sm divide-y border rounded-md">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between px-3 py-2 gap-2">
              <div>
                <div className="font-medium">
                  {TIPO_CONSENTIMIENTO[r.tipo] ?? r.tipo}
                  {r.version && <span className="ml-1 text-xs text-muted-foreground font-mono">{r.version}</span>}
                  {r.revocado
                    ? <span className="ml-2 rounded bg-urgente-soft px-2 py-0.5 text-xs text-urgente">revocado</span>
                    : <span className="ml-2 rounded bg-estable-soft px-2 py-0.5 text-xs text-estable">vigente</span>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {r.otorgante === 'representante' ? `Representante: ${r.otorgante_nombre ?? ''}` : 'Titular'} · {r.medio} · base: {BASE_LEGAL[r.base_legal] ?? r.base_legal} · {fmt(r.aceptado_at)}
                  {r.revocado && r.revocado_at && <> · revocado {fmt(r.revocado_at)}</>}
                </div>
              </div>
              {!r.revocado && (
                <button className="text-red-500 hover:text-urgente inline-flex items-center gap-1 text-xs" title="Revocar" aria-label="Revocar"
                  onClick={async () => { if (!(await dlg.confirmar('¿Revocar este consentimiento?', { detalle: 'El titular puede hacerlo en cualquier momento.', peligro: true }))) return; revocar.mutate(r.id); }}>
                  <ShieldOff className="w-4 h-4" /> Revocar
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {abrir && (
        <RegistrarModal pacienteId={pacienteId} portal={portal} onClose={() => setAbrir(false)}
          onSaved={() => { setAbrir(false); void qc.invalidateQueries({ queryKey: key }); }} yaTiene={activos.length > 0} />
      )}
    </section>
  );
}

function RegistrarModal({ pacienteId, portal, onClose, onSaved }: { pacienteId?: string; portal: boolean; onClose: () => void; onSaved: () => void; yaTiene: boolean }) {
  const { data: modelos = [] } = useQuery<ConsentModelo[]>({
    queryKey: ['med-consent-modelos'], queryFn: () => medRpc<ConsentModelo[]>('med_consentimiento_modelos', {}),
  });
  const vigentes = useMemo(() => modelos.filter((m) => m.vigente), [modelos]);
  const [modeloId, setModeloId] = useState('');
  const modelo = useMemo(() => vigentes.find((m) => m.id === modeloId) ?? vigentes.find((m) => m.tipo === 'datos_salud') ?? vigentes[0], [vigentes, modeloId]);
  const [aceptados, setAceptados] = useState<Record<string, boolean>>({});
  const [otorgante, setOtorgante] = useState<'titular' | 'representante'>('titular');
  const [rep, setRep] = useState({ nombre: '', identificacion: '', relacion: '' });
  const [firma, setFirma] = useState('');

  const items = modelo?.items ?? [];
  const faltanReq = items.some((it) => it.requerido && !aceptados[it.clave]);

  const guardar = useMutation({
    mutationFn: () => medRpc('med_registrar_consentimiento', {
      _data: {
        paciente_id: pacienteId,
        tipo: modelo?.tipo ?? 'datos_salud',
        modelo_id: modelo?.id,
        items: items.filter((it) => aceptados[it.clave]),
        otorgante,
        otorgante_nombre: otorgante === 'representante' ? rep.nombre : null,
        otorgante_identificacion: otorgante === 'representante' ? rep.identificacion : null,
        otorgante_relacion: otorgante === 'representante' ? rep.relacion : null,
        medio: portal ? 'portal' : 'presencial',
        firma: firma || null,
      },
    }),
    onSuccess: () => { toast.success('Consentimiento registrado'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Registrar consentimiento" onClose={onClose} wide>
      <div className="space-y-3">
        {vigentes.length > 1 && (
          <Campo label="Tipo / plantilla">
            <select className={fieldCls} value={modelo?.id ?? ''} onChange={(e) => { setModeloId(e.target.value); setAceptados({}); }}>
              {vigentes.map((m) => <option key={m.id} value={m.id}>{TIPO_CONSENTIMIENTO[m.tipo] ?? m.tipo} · {m.version}</option>)}
            </select>
          </Campo>
        )}

        {!modelo ? (
          <p className="text-sm text-pendiente">No hay una plantilla de consentimiento vigente. Pide al administrador que cree una en Catálogos → Consentimientos.</p>
        ) : (
          <>
            <div>
              <h4 className="font-medium">{modelo.titulo}</h4>
              <div className="mt-1 max-h-44 overflow-y-auto rounded-md border bg-muted/30 p-3 text-sm whitespace-pre-wrap">{modelo.texto}</div>
            </div>

            <div className="space-y-1.5">
              {items.map((it) => (
                <label key={it.clave} className="flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-1" checked={!!aceptados[it.clave]} onChange={(e) => setAceptados((a) => ({ ...a, [it.clave]: e.target.checked }))} />
                  <span>{it.etiqueta} {it.requerido && <span className="text-urgente">*</span>}</span>
                </label>
              ))}
            </div>

            {!portal && (
              <div className="grid grid-cols-4 gap-3 items-end border-t pt-3">
                <Campo label="Otorga">
                  <select className={fieldCls} value={otorgante} onChange={(e) => setOtorgante(e.target.value as 'titular' | 'representante')}>
                    <option value="titular">El titular (paciente)</option>
                    <option value="representante">Un representante / tutor</option>
                  </select>
                </Campo>
                {otorgante === 'representante' && <>
                  <Campo label="Nombre"><input className={fieldCls} value={rep.nombre} onChange={(e) => setRep({ ...rep, nombre: e.target.value })} /></Campo>
                  <Campo label="Identificación"><input className={fieldCls} value={rep.identificacion} onChange={(e) => setRep({ ...rep, identificacion: e.target.value })} /></Campo>
                  <Campo label="Relación"><input className={fieldCls} value={rep.relacion} onChange={(e) => setRep({ ...rep, relacion: e.target.value })} placeholder="Madre, tutor legal…" /></Campo>
                </>}
              </div>
            )}

            <FirmaCanvas value={firma} onChange={setFirma} label="Firma (opcional)" />

            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={faltanReq || (otorgante === 'representante' && !rep.nombre.trim())} onClick={() => guardar.mutate()}>
                Aceptar y registrar
              </Button>
            </div>
            {faltanReq && <p className="text-xs text-pendiente text-right">Debes aceptar las cláusulas obligatorias (*) para continuar.</p>}
          </>
        )}
      </div>
    </Modal>
  );
}
